import type { RootState } from "@/app/store";
import {
  reorderLeaseCompField as reorderLeaseCompFieldAction,
  setFieldRulesLoading,
  setLeaseCompFieldRules,
  updateLeaseCompFieldRule as updateLeaseCompFieldRuleAction,
  updateLeaseCompFieldRuleLabel as updateLeaseCompFieldRuleLabelAction,
} from "@/features/survey/leaseCompsSlice";
import { updateCurrentSurvey } from "@features/survey/surveySlice";
import type { LeaseCompFieldRule } from "@survey-types/survey.core.types";
import { surveyApi } from "./base";
import { surveyCoreApi } from "./survey-core";

// Lease Comp Field Rules API - handles lease comp field visibility and ordering
export const leaseCompFieldRulesApi = surveyApi
  .enhanceEndpoints({
    addTagTypes: ["LeaseCompFieldRules"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      /**
       * Fetch lease comp field rules for a survey
       * Returns the list of available fields and their display settings
       */
      getLeaseCompFieldRules: build.query<LeaseCompFieldRule[], string>({
        query: (surveyId) => ({
          url: `/leaseComp/${surveyId}/leasecompfieldrule`,
          method: "GET",
        }),
        providesTags: (_result, _error, surveyId) => [
          { type: "LeaseCompFieldRules", id: surveyId },
        ],
        async onQueryStarted(_surveyId, { dispatch, queryFulfilled }) {
          dispatch(setFieldRulesLoading(true));
          try {
            const { data } = await queryFulfilled;
            dispatch(setLeaseCompFieldRules(data));
          } catch {
            dispatch(setLeaseCompFieldRules([]));
          } finally {
            dispatch(setFieldRulesLoading(false));
          }
        },
      }),

      reorderLeaseCompField: build.mutation<
        void,
        {
          surveyId: string;
          order: number;
          fieldId: string;
          leaseCompLayoutId?: string;
        }
      >({
        query: ({ surveyId, order, fieldId }) => ({
          url: `/leaseComp/${surveyId}/leasecompfieldrule/reorder`,
          method: "PATCH",
          body: { surveyId, order, key: fieldId },
        }),
        async onQueryStarted(
          { surveyId, order, fieldId, leaseCompLayoutId },
          { dispatch, queryFulfilled, getState },
        ) {
          const state = getState() as unknown as RootState;
          const fieldRules = state.leaseComps.fieldRules;

          // Compute visual sourceIndex from the active-field sorted list,
          // matching how visibleFields is derived in the component.
          const activeFields = [...fieldRules]
            .filter((f) => f.isActive)
            .sort((a, b) => a.order - b.order);
          const sourceIndex = activeFields.findIndex((f) => f.key === fieldId);
          if (sourceIndex === -1) return;

          // Optimistically update Redux store
          dispatch(
            reorderLeaseCompFieldAction({
              sourceIndex,
              destinationIndex: order,
            }),
          );

          // Optimistically update RTK cache
          const cachePatch = dispatch(
            leaseCompFieldRulesApi.util.updateQueryData(
              "getLeaseCompFieldRules",
              surveyId,
              (draft) => {
                const currentIdx = draft.findIndex((f) => f.key === fieldId);
                if (currentIdx !== -1) {
                  const [item] = draft.splice(currentIdx, 1);
                  draft.splice(order, 0, item);
                }
              },
            ),
          );

          try {
            await queryFulfilled;
            const layoutId = state.survey.currentSurvey?.leaseCompLayoutId;

            if (layoutId !== leaseCompLayoutId) {
              dispatch(updateCurrentSurvey({ leaseCompLayoutId }));
              dispatch(
                surveyCoreApi.endpoints.updateSurvey.initiate({
                  id: surveyId,
                  leaseCompLayoutId,
                }),
              );
            }
          } catch {
            cachePatch.undo();
            // Rollback: reverse the optimistic reorder using the same visual indices
            dispatch(
              reorderLeaseCompFieldAction({
                sourceIndex: order,
                destinationIndex: sourceIndex,
              }),
            );
          }
        },
      }),
      updateLeaseCompFieldRule: build.mutation<
        void,
        {
          surveyId: string;
          fieldRule: LeaseCompFieldRule;
          leaseCompLayoutId?: string;
        }
      >({
        query: ({ surveyId, fieldRule }) => ({
          url: `/leaseComp/${surveyId}/leasecompfieldrule`,
          method: "PATCH",
          body: fieldRule,
        }),
        async onQueryStarted(
          { surveyId, fieldRule, leaseCompLayoutId },
          { dispatch, queryFulfilled, getState },
        ) {
          const state = getState() as unknown as RootState;
          const originalField = state.leaseComps.fieldRules.find(
            (f) => f.key === fieldRule.key,
          );

          // Optimistically update Redux store
          dispatch(
            updateLeaseCompFieldRuleAction({
              key: fieldRule.key,
              isActive: fieldRule.isActive,
              order: fieldRule.order,
            }),
          );
          dispatch(
            updateLeaseCompFieldRuleLabelAction({
              key: fieldRule.key,
              displayName: fieldRule.displayName,
            }),
          );

          // Optimistically update RTK cache
          const cachePatch = dispatch(
            leaseCompFieldRulesApi.util.updateQueryData(
              "getLeaseCompFieldRules",
              surveyId,
              (draft) => {
                const target = draft.find((f) => f.key === fieldRule.key);
                if (target) {
                  Object.assign(target, fieldRule);
                }
              },
            ),
          );

          try {
            await queryFulfilled;
            const layoutId = state.survey.currentSurvey?.leaseCompLayoutId;

            if (layoutId !== leaseCompLayoutId) {
              dispatch(updateCurrentSurvey({ leaseCompLayoutId }));
              dispatch(
                surveyCoreApi.endpoints.updateSurvey.initiate({
                  id: surveyId,
                  leaseCompLayoutId,
                }),
              );
            }
          } catch {
            cachePatch.undo();
            if (originalField) {
              dispatch(
                updateLeaseCompFieldRuleAction({
                  key: originalField.key,
                  isActive: originalField.isActive,
                  order: originalField.order,
                }),
              );
              dispatch(
                updateLeaseCompFieldRuleLabelAction({
                  key: originalField.key,
                  displayName: originalField.displayName,
                }),
              );
            }
          }
        },
      }),
      updateLeaseCompFieldRulesBulk: build.mutation<
        void,
        {
          surveyId: string;
          fieldRules: LeaseCompFieldRule[];
          leaseCompLayoutId?: string;
        }
      >({
        query: ({ surveyId, fieldRules }) => ({
          url: `/leaseComp/leasecompfieldrule/${surveyId}/bulkUpdate`,
          method: "PATCH",
          body: fieldRules,
        }),
        async onQueryStarted(
          { surveyId, fieldRules, leaseCompLayoutId },
          { dispatch, queryFulfilled, getState },
        ) {
          const state = getState() as unknown as RootState;
          const originalFieldRules = state.leaseComps.fieldRules.map(
            (field) => ({
              ...field,
            }),
          );

          // Optimistically update Redux store
          dispatch(setLeaseCompFieldRules(fieldRules));

          // Optimistically update RTK cache
          const cachePatch = dispatch(
            leaseCompFieldRulesApi.util.updateQueryData(
              "getLeaseCompFieldRules",
              surveyId,
              (draft) => {
                draft.splice(0, draft.length, ...fieldRules);
              },
            ),
          );

          try {
            await queryFulfilled;
            const layoutId = state.survey.currentSurvey?.leaseCompLayoutId;

            if (layoutId !== leaseCompLayoutId) {
              dispatch(updateCurrentSurvey({ leaseCompLayoutId }));
              // Persist selected lease comp layout on survey so Survey cache invalidates.
              dispatch(
                surveyCoreApi.endpoints.updateSurvey.initiate({
                  id: surveyId,
                  leaseCompLayoutId,
                }),
              );
            }
          } catch {
            cachePatch.undo();
            dispatch(setLeaseCompFieldRules(originalFieldRules));
          }
        },
      }),
    }),
  });

export const {
  useGetLeaseCompFieldRulesQuery,
  useReorderLeaseCompFieldMutation,
  useUpdateLeaseCompFieldRuleMutation,
  useUpdateLeaseCompFieldRulesBulkMutation,
} = leaseCompFieldRulesApi;
